import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ImportSource, ImportStatus, TrendDto, TrendStatus } from '@afiliados/shared';

export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '—';
export const fmtDateTimeSec = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '—';
export const fmtDate = (iso: string | null | undefined) =>
  iso ? format(new Date(iso), 'dd/MM/yyyy', { locale: ptBR }) : '—';
/** "2026-04-05" (dia sem fuso) → "05/04/2026" */
export const fmtDay = (day: string | null | undefined) => {
  if (!day) return '—';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
};
export const fmtRelative = (iso: string | null | undefined) =>
  iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR }) : '—';

const money = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const moneyCompact = new Intl.NumberFormat('pt-BR', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const integer = new Intl.NumberFormat('pt-BR');

/** Valores em dólar com separadores brasileiros, como na planilha: "1.106,74". */
export const fmtMoney = (v: number | null | undefined) => (v == null ? '—' : money.format(v));
export const fmtUsd = (v: number | null | undefined) =>
  v == null ? '—' : `US$ ${money.format(v)}`;
export const fmtUsdCompact = (v: number | null | undefined) =>
  v == null ? '—' : `US$ ${moneyCompact.format(v)}`;
export const fmtInt = (v: number | null | undefined) => (v == null ? '—' : integer.format(v));
export const fmtPct = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : `${(v * 100).toFixed(digits).replace('.', ',')}%`;
export const fmtBytes = (b: number) =>
  b < 1024
    ? `${b} B`
    : b < 1024 * 1024
      ? `${(b / 1024).toFixed(0)} KB`
      : `${(b / 1024 / 1024).toFixed(1)} MB`;

export const IMPORT_STATUS_LABEL: Record<ImportStatus, string> = {
  active: 'Ativa',
  superseded: 'Substituída',
  conflict: 'Em conflito',
};
export const IMPORT_STATUS_VARIANT: Record<ImportStatus, string> = {
  active: 'bg-success/15 text-green-700 dark:text-green-300',
  superseded: 'bg-muted text-muted-foreground',
  conflict: 'bg-warning/20 text-amber-700 dark:text-amber-300',
};
export const IMPORT_SOURCE_LABEL: Record<ImportSource, string> = {
  manual: 'Upload',
  email: 'E-mail',
};

/** Para selects: em ordem alfabética do que o usuário lê, com acentos e caixa tratados. */
export const sortByName = <T extends { name: string }>(items: readonly T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

/** Mês por extenso para selects: "abril/2026". */
export function monthLong(key: string): string {
  const [y, m] = key.split('-');
  const names = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  return `${names[Number(m) - 1] ?? m}/${y}`;
}

// ---------- Variação mês a mês (rastreamento de calor) ----------
export const TREND_LABEL: Record<TrendStatus, string> = {
  up_strong: 'Alta forte (50% ou mais)',
  up: 'Alta (20% ou mais)',
  flat: 'Estável',
  down: 'Queda (20% ou mais)',
  down_strong: 'Queda forte (50% ou mais)',
  zero: 'Zerou depois de vender',
  new: 'Primeiro mês com venda',
};

/** Fundo da célula; o texto escurece/clareia para manter o contraste nos dois temas. */
export const TREND_CELL_CLASS: Record<TrendStatus, string> = {
  up_strong: 'bg-emerald-500/40 text-emerald-950 dark:text-emerald-50',
  up: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100',
  flat: '',
  down: 'bg-red-500/15 text-red-900 dark:text-red-100',
  down_strong: 'bg-red-500/40 text-red-950 dark:text-red-50',
  zero: 'bg-amber-400/45 text-amber-950 dark:text-amber-50',
  new: 'bg-sky-500/25 text-sky-950 dark:text-sky-50',
};

/** Cor do texto da coluna Tendência. */
export const TREND_TEXT_CLASS: Record<TrendStatus, string> = {
  up_strong: 'text-emerald-700 dark:text-emerald-300',
  up: 'text-emerald-700 dark:text-emerald-300',
  flat: 'text-muted-foreground',
  down: 'text-red-700 dark:text-red-300',
  down_strong: 'text-red-700 dark:text-red-300',
  zero: 'text-amber-700 dark:text-amber-300',
  new: 'text-sky-700 dark:text-sky-300',
};

/** "+35%", "−62%", "zerou", "novo" ou "—". */
export const fmtTrend = (t: TrendDto | null | undefined): string => {
  if (!t || !t.status) return '—';
  if (t.status === 'new') return 'novo';
  if (t.status === 'zero') return 'zerou';
  if (t.ratio == null) return '—';
  const pct = Math.round(t.ratio * 100);
  return `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(pct)}%`;
};
